import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  IconBrandGit,
  IconBriefcase,
  IconDashboard,
  IconFlask,
  IconFolder,
  IconGitCommit,
  IconLogout,
  IconMenu2,
  IconRocket,
  IconSearch,
  IconSettings,
  IconUser,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface ShellProps {
  user: string;
  onLogout: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof IconDashboard;
}

interface NavSection {
  label: string;
  items: readonly NavItem[];
}

// Per-VCS sidebar sections: "Perforce" for the p4-backed surfaces,
// "Lore" for the pilot (appended at render time only when the backend
// reports the pilot enabled — see loreProbe in AppShell), plus
// "Overview" and "Manage" bookends. New sources (e.g. a git service)
// get their own section rather than growing Perforce's.
const NAV_SECTIONS: readonly NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Status', icon: IconDashboard }],
  },
  {
    label: 'Perforce',
    items: [
      { to: '/getting-started/perforce', label: 'Getting Started', icon: IconRocket },
      { to: '/changes', label: 'Changes', icon: IconGitCommit },
      { to: '/depot', label: 'Depot', icon: IconFolder },
      { to: '/users', label: 'Users', icon: IconUsers },
      { to: '/workspaces', label: 'Workspaces', icon: IconBriefcase },
    ],
  },
  {
    label: 'Manage',
    items: [{ to: '/admin', label: 'Admin', icon: IconSettings }],
  },
];

const LORE_SECTION: NavSection = {
  label: 'Lore',
  items: [
    { to: '/getting-started/lore', label: 'Getting Started', icon: IconRocket },
    { to: '/lore', label: 'Repos', icon: IconFlask },
  ],
};

const GIT_SECTION: NavSection = {
  label: 'Git',
  items: [{ to: '/git', label: 'Repos', icon: IconBrandGit }],
};

export function AppShell({ user, onLogout }: ShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [omniQ, setOmniQ] = useState('');

  // Lore pilot nav item appears only when the backend has the pilot
  // enabled (probe 503s otherwise). Cheap, cached, and never retried —
  // tearing the pilot down hides the UI without a frontend rebuild.
  const loreProbe = useQuery({
    queryKey: ['lore.repos'],
    queryFn: api.loreRepos,
    retry: false,
    staleTime: 5 * 60_000,
  });
  // Same probe pattern for the Forgejo integration: section appears only
  // when /api/git/repos answers (503 when FORGEJO_TOKEN is unset).
  const gitProbe = useQuery({
    queryKey: ['git.repos'],
    queryFn: api.gitRepos,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const navSections: readonly NavSection[] = [
    ...NAV_SECTIONS.slice(0, 2),
    ...(loreProbe.isSuccess ? [LORE_SECTION] : []),
    ...(gitProbe.isSuccess ? [GIT_SECTION] : []),
    ...NAV_SECTIONS.slice(2),
  ];

  const isActive = (to: string): boolean => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      {/* ============================================================
          Top header — 56 px, full-width, bottom border separates
          from main + sidebar zones.
          ============================================================ */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-neutral-800 bg-card px-4">
        <div className="flex items-center gap-2">
          {/* Burger — mobile only. Toggles the sidebar overlay. */}
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground hover:bg-neutral-800 hover:text-foreground md:hidden"
            aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          >
            {navOpen ? <IconX size={16} /> : <IconMenu2 size={16} />}
          </button>
          <span className="text-sm font-semibold tracking-tight text-foreground">
            Theia Dashboard
          </span>
          <Badge tone="orange" variant="subtle" className="ml-1">
            dev
          </Badge>
        </div>

        {/* Omni search — every indexed source (p4, Lore, git). Enter → /search. */}
        <form
          className="mx-4 hidden min-w-0 flex-1 justify-center sm:flex"
          onSubmit={(e) => {
            e.preventDefault();
            const q = omniQ.trim();
            if (q.length >= 2) navigate(`/search?q=${encodeURIComponent(q)}`);
          }}
        >
          <label className="relative w-full max-w-md">
            <IconSearch size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={omniQ}
              onChange={(e) => setOmniQ(e.target.value)}
              placeholder="Search all files… (p4, Lore, git)"
              aria-label="Search all files"
              className="h-8 w-full rounded-md border border-neutral-800 bg-background pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:border-theia-600 focus:outline-none"
            />
          </label>
        </form>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-md transition-colors hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="User menu"
            >
              <Avatar size={28} tone="orange">
                {user.slice(0, 2).toUpperCase()}
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
            <DropdownMenuItem className="cursor-default">
              <IconUser />
              {user}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={onLogout}>
              <IconLogout />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ============================================================
            Sidebar.
            - md+: persistent 260 px left rail, always visible
            - <md: slides in over content as an overlay with backdrop
            CF uses the same pattern (responsive collapse).
            ============================================================ */}
        {navOpen && (
          // Backdrop, mobile-only. Tap to close. md:hidden so
          // desktop never sees it.
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="fixed inset-0 top-14 z-30 bg-black/60 md:hidden"
          />
        )}
        <aside
          className={cn(
            'fixed inset-y-14 left-0 z-40 w-64 shrink-0 overflow-y-auto border-r border-neutral-800 bg-card py-4',
            'transition-transform duration-200',
            'md:static md:inset-auto md:translate-x-0',
            navOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          {navSections.map((section) => (
            <div key={section.label} className="px-2 pb-4">
              <div className="px-3 pb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {section.label}
              </div>
              <nav className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setNavOpen(false)}
                      className={cn(
                        'group relative flex items-center gap-2 rounded-sm px-3 py-1.5 text-[13px] transition-colors',
                        active
                          ? 'bg-theia-950/40 text-theia-300'
                          : 'text-neutral-300 hover:bg-neutral-900 hover:text-foreground',
                      )}
                    >
                      {active && (
                        // 2 px left bar in the brand orange — the
                        // single "active" cue everywhere in chrome.
                        <span
                          aria-hidden
                          className="absolute inset-y-1 left-0 w-[2px] rounded-r bg-theia-600"
                        />
                      )}
                      <Icon
                        size={14}
                        stroke={1.75}
                        className={cn(
                          'shrink-0 transition-colors',
                          active
                            ? 'text-theia-400'
                            : 'text-muted-foreground group-hover:text-foreground',
                        )}
                      />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          ))}
        </aside>

        {/* ============================================================
            Main content. Keyed by pathname for the page-fade
            transition (CSS animation defined in main.css).
            ============================================================ */}
        <main className="flex-1 overflow-y-auto">
          <div
            key={location.pathname}
            className="theia-page-fade p-4 md:p-6"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// Re-export under the old name so existing imports
// (`import { AppShell }`) keep working without changes.
export default AppShell;
